import React, { useEffect, useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import { editObject, getObjectsWithFilter, getObject } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import { useAuth } from '../../../context/AuthContext'
import { useLocation } from 'react-router-dom'
import Form from '../../form/Form'
import InterventionPDF from './InterventionPDF'
import { buildInterventionPayload, convertDocumentAndUploadFile } from '../../../utils/documentManager'
import { getInterventionFields } from './InterventionFields'
import useSaveEntity from '../../hooks/useSaveEntity'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import Toolbar from '../../toolbar/Toolbar'

const ROUTES = {
  EDIT: '/interventions/edit',
  VIEW: '/interventions/view',
  ADD: '/interventions/add'
}

const EditIntervention = () => {
  const history = useNavigate()
  const [intervention, setIntervention] = useState({ lines: [] })
  const [company, setCompany] = useState({})
  const [img, setImg] = useState('')
  const [isSubmit, setIsSubmit] = useState(false)
  const { user } = useAuth()
  const location = useLocation()
  const [settings, setSettings] = useState({})
  const path = location.pathname
  const interventionId = location.state?.id

  const toolbarOptions = useMemo(
    () => ({
      title: () => {
        return path.includes('/add') ? 'Nouvelle Intervention' : `Intervention ${intervention.intervention_number}`
      }
    }),
    [intervention, path]
  )

  const mode = useMemo(() => {
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path])

  const interventionQuery =
    mode === 'edit' ? { key: 'intervention', fn: getObject, args: ['interventions', interventionId] } : null

  const queries = [
    { key: 'settings', fn: getObjectsWithFilter, args: ['settings', { company_id: user.company_id }] },
    { key: 'company', fn: getObject, args: ['company', user.company_id] },
    interventionQuery
  ].filter(Boolean)

  const { data: fetched, loading: loadingEntities } = useFetchEntities(queries)

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'interventions',
    mode,
    id: interventionId,
    user,
    settingsKey: 'intervention_number',
    counterName: 'interventions_nb',
    updateCounter: true,
    editFn: editObject,
    contractLineName: 'Interventions',
    args: [intervention],
    onSuccessRedirect: (id) => history(location.state?.history || ROUTES.VIEW, { state: { id } }),
    onSuccess: (res) => {
      setIntervention(res.data)
      convertDocumentAndUploadFile(
        <InterventionPDF intervention_param={res.data} company_param={company} logo_param={img} settings={settings} />,
        `intervention_${res.data.intervention_number}`,
        user,
        'intervention',
        'interventions',
        res.data
      )
      setIsSubmit(false)
    },
    onError: () => setIsSubmit(false),
    buildPayload: buildInterventionPayload
  })

  const memoizedFields = useMemo(() => getInterventionFields(user, location), [user, location])

  const options = useMemo(() => ({ fields: memoizedFields, data: intervention }), [memoizedFields, intervention])

  useEffect(() => {
    if (!loadingEntities) {
      if (fetched.settings) setSettings(fetched.settings)
      if (fetched.company) {
        setCompany(fetched.company)
        const attach = fetched.company.attachments?.find((a) => a.type === 'logo')
        if (attach?.file?.data) {
          const blob = new Blob([new Uint8Array(attach.file.data)])
          const reader = new FileReader()
          reader.onloadend = () => {
            const base64 = reader.result?.toString().split(',')[1] ?? ''
            setImg(base64)
          }
          reader.readAsDataURL(blob)
        }
      }
      if (fetched.intervention) setIntervention(fetched.intervention)
    }
  }, [fetched, loadingEntities])

  useEffect(() => {
    if (isSubmit) {
      saveEntity({ data: intervention })
    }
  }, [isSubmit, intervention])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flex: 1
      }}
    >
      <Toolbar options={toolbarOptions} object={intervention} />
      <Form
        options={options}
        mode='edit'
        setDataParent={setIntervention}
        setIsSubmitParent={setIsSubmit}
        loading={loading}
      />
    </Box>
  )
}

export default EditIntervention
